import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
  useCallback,
} from "react";
import { Container, Text } from "@pixi/react";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import {
  FONT_CONFIG,
  USER_INTERFACE_BUTTON_CONFIG,
  TEXT_STYLE_CONFIG,
} from "../../../../textureConfigs";
import {
  selectRespinsLeft,
  selectFeatureSpinComplete,
  selectShowHoldAndWinCollector,
  selectIsHoldAndWinCompletionReload,
  selectHoldAndWinActive,
} from "../../../../State/selectors";
import { useHoldAndWinControl } from "../../../../Contexts/HoldAndWinControlContext";

const RespinsDisplay = ({ toolbar }) => {
  const { t } = useTranslation();
  const {
    device,
    orientation,
    getResponsiveConfig,
    uiSymbolScale,
    dimensions,
  } = useDimensions();
  const respinsLeft = useSelector(selectRespinsLeft);
  const featureSpinComplete = useSelector(selectFeatureSpinComplete);
  const showHoldAndWinCollector = useSelector(selectShowHoldAndWinCollector);
  const isHoldAndWinCompletionReload = useSelector(selectIsHoldAndWinCompletionReload);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  const { registerProgressBarCallback } = useHoldAndWinControl();

  // Track previous value to detect changes immediately
  const prevRespinsRef = useRef(null);
  const [displayValue, setDisplayValue] = useState(respinsLeft);
  const localUpdatedRef = useRef(false); // Track if updated during spin

  // Get button dimensions using the same system as PopiButtonAutoplay
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  // Get unified respins configuration (font multiplier, y position)
  // Font multipliers match PopiBalanceDisplay exactly
  const getRespinsConfig = () => {
    let fontMultiplier = 1.0;
    let y = 0;

    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.0; // Match PopiBalanceDisplay
        y = -dimensions.height / 5.2;
      } else {
        fontMultiplier = 1.1; // Match PopiBalanceDisplay
        y = -dimensions.height / 2 + buttonConfig.height * 4.0;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        fontMultiplier = 1.65; // Match PopiBalanceDisplay
        y = -dimensions.height / 5.1;
      } else {
        fontMultiplier = 1.25; // Match PopiBalanceDisplay
        y = toolbar.height / 2;
      }
    } else {
      // Desktop
      fontMultiplier = 1.0; // Match PopiBalanceDisplay
      y = toolbar.height / 2;
    }

    return { fontMultiplier, y };
  };

  // Callback when a coin lands - update display ONLY on first coin
  const handleCoinLanded = useCallback(() => {
    // Only update on FIRST coin landing during spin
    if (!featureSpinComplete && !localUpdatedRef.current) {
      // Reset to 3 when first coin lands
      setDisplayValue(3);
      prevRespinsRef.current = 3;
      localUpdatedRef.current = true; // Mark as updated
    }
  }, [featureSpinComplete]);

  // Update display value when respinsLeft changes from Redux
  useEffect(() => {
    if (
      respinsLeft !== undefined &&
      respinsLeft !== null &&
      prevRespinsRef.current !== respinsLeft
    ) {
      // Determine if this is a decrease (spin start) or increase (coin landed)
      const isDecrease =
        prevRespinsRef.current !== null && respinsLeft < prevRespinsRef.current;
      const isIncrease =
        prevRespinsRef.current !== null && respinsLeft > prevRespinsRef.current;

      // Update immediately if:
      // 1. Spin is complete (server reconciliation)
      // 2. Counter is decreasing (spin started, using a respin credit)
      // 3. Counter hasn't been updated locally during this spin yet (prevents double update)
      if (
        featureSpinComplete ||
        isDecrease ||
        (isIncrease && !localUpdatedRef.current)
      ) {
        setDisplayValue(respinsLeft);
        prevRespinsRef.current = respinsLeft;
      }
    }
  }, [respinsLeft, featureSpinComplete]);

  // Initialize display value on mount
  useEffect(() => {
    if (
      respinsLeft !== undefined &&
      respinsLeft !== null &&
      prevRespinsRef.current === null
    ) {
      setDisplayValue(respinsLeft);
      prevRespinsRef.current = respinsLeft;
    }
  }, [respinsLeft]);

  // Register callback for coin landings
  useEffect(() => {
    const unregister = registerProgressBarCallback(handleCoinLanded);
    return unregister; // Cleanup function removes this specific callback
  }, [registerProgressBarCallback, handleCoinLanded]);

  // Reset local update flag based on spin state
  useEffect(() => {
    if (featureSpinComplete) {
      // Spin finished - reset flag for next spin
      localUpdatedRef.current = false;
    }
  }, [featureSpinComplete]);

  const respinsConfig = getRespinsConfig();
  const baseFontSize = 36; // Match PopiBalanceDisplay exactly
  const responsiveFontSize = baseFontSize * respinsConfig.fontMultiplier;
  const idealFontSize = Math.floor(responsiveFontSize * uiSymbolScale);
  const LABEL_FONT_SIZE = Math.floor(idealFontSize * 0.9); // Slightly smaller for label

  // Prepare text content
  const respinsLabelText =
    t("respins_left").toLowerCase().charAt(0).toUpperCase() +
    t("respins_left").toLowerCase().slice(1);
  const respinsAmountText = `${displayValue}/3`;

  const respinsLabelStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: LABEL_FONT_SIZE,
        fontWeight: "500",
        fill: "#ffffff", // White color same as value
        align: "right",
        ...TEXT_STYLE_CONFIG.OUTLINE, // Black outline with 0.5 opacity and 2px thickness
      }),
    [LABEL_FONT_SIZE]
  );

  // Calculate maximum width based on "3/3" format to stabilize component width
  const maxRespinsWidth = useMemo(() => {
    const maxRespinsText = "3/3"; // Maximum format
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
    });
    const maxRespinsMetrics = TextMetrics.measureText(
      maxRespinsText,
      tempStyle
    );

    return maxRespinsMetrics.width;
  }, [idealFontSize]);

  /**
   * Auto-scaling font size calculation for respins amount
   * Similar to PopiBalanceDisplay: measures actual text and scales down if it exceeds max width
   */
  const finalAmountFontSize = useMemo(() => {
    // Measure actual respins text with ideal font size
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "700",
    });
    const metrics = TextMetrics.measureText(respinsAmountText, tempStyle);

    // If text exceeds max width, scale down proportionally
    if (metrics.width > maxRespinsWidth) {
      const scale = maxRespinsWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }

    // Return ideal font size if it fits
    return idealFontSize;
  }, [respinsAmountText, idealFontSize, maxRespinsWidth]);

  const respinsAmountStyle = useMemo(
    () =>
      new TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: finalAmountFontSize,
        fontWeight: "700", // Bigger font weight for value
        fill: "#ffffff", // White for numbers
        align: "right",
        ...TEXT_STYLE_CONFIG.OUTLINE, // Black outline with 0.5 opacity and 2px thickness
      }),
    [finalAmountFontSize]
  );

  // Measure text dimensions for positioning - Dynamic positioning based on actual text widths
  const labelMetrics = TextMetrics.measureText(
    respinsLabelText,
    respinsLabelStyle
  );
  const amountMetrics = TextMetrics.measureText(
    respinsAmountText,
    respinsAmountStyle
  );

  const totalHeight = (labelMetrics.height + amountMetrics.height) * 0.95; // Reduce gap by 5%
  // Use the wider of the two texts for right alignment
  const maxWidth = Math.max(labelMetrics.width, maxRespinsWidth);
  const labelY = -totalHeight / 2 + labelMetrics.height / 2;
  const amountY = totalHeight / 2 - amountMetrics.height / 2;

  // Calculate dynamic X position based on text width
  // Position from right edge, accounting for the actual text width
  const calculateXPosition = () => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return toolbar.width / 2 - buttonConfig.width * 1.6 - maxWidth;
      } else {
        return toolbar.width - buttonConfig.width * 1 - maxWidth;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return toolbar.width / 2 - buttonConfig.width * 1.85 - maxWidth;
      } else {
        return toolbar.width - buttonConfig.width * 4 - maxWidth;
      }
    } else {
      // Desktop
      return toolbar.width - buttonConfig.width * 5.5 - maxWidth;
    }
  };

  const xPosition = calculateXPosition();

  // Hide display when Hold and Win becomes inactive (same as counter)
  // Counter uses visible={holdAndWinActive}, so we hide here too
  if (!holdAndWinActive) {
    return null;
  }

  // Hide display when collection starts (same timing as counter removal)
  // Both counter and respins display are removed together to prevent re-renders
  // Counter is hidden via hide() method when collection starts
  if (showHoldAndWinCollector) {
    return null;
  }

  // CRITICAL: Don't render respins display for completion reload (feature already completed)
  // When user reloads after feature ends, don't show the display at all
  if (isHoldAndWinCompletionReload) {
    return null;
  }

  return (
    <Container
      name="respins_display"
      x={xPosition}
      y={respinsConfig.y}
      zIndex={5}
    >
      <Text
        key={`respins-label-column`}
        text={respinsLabelText}
        style={respinsLabelStyle}
        anchor={{ x: 1, y: 0.5 }}
        x={maxWidth}
        y={labelY}
      />
      <Text
        key={`respins-amount-column`}
        text={respinsAmountText}
        style={respinsAmountStyle}
        anchor={{ x: 1, y: 0.5 }}
        x={maxWidth}
        y={amountY}
      />
    </Container>
  );
};

export default RespinsDisplay;
